import {
  Users,
  Heart,
  Truck,
  MessageSquare,
  DollarSign,
  Briefcase,
  Handshake,
  Settings,
  Wallet,
  Gift,
  Target,
  Smile,
  Frown,
  Zap,
  Package,
  Building2,
  Shuffle,
  Sparkles,
  AlertCircle,
  Clock,
  BarChart3,
  AlertTriangle,
  User,
  Cloud,
  Sun,
  Star,
  type LucideIcon,
} from 'lucide-react'
import {
  CANVAS_TEMPLATES as SHARED_TEMPLATES,
  type CanvasTemplateDef,
  type CanvasSectionDef,
  type CanvasAttribution,
} from '@ispot/shared'
import {
  inferCanvasSectionTheme,
  getCanvasSectionThemeStyles,
  type CanvasSectionTheme,
} from './canvasTheme'

// Canvas section definition (with React icon component)
export interface CanvasSection {
  id: string
  title: string
  icon: LucideIcon
  theme: CanvasSectionTheme
  color: string
  bgColor: string
  classUris: string[]
  gridArea: string
  description: string
  sectionType?: 'default' | 'photo'
}

// Canvas template definition (with React icon components)
export interface CanvasTemplate {
  id: string
  name: string
  description: string
  gridColumns: number
  gridRows: number
  gridAreas: string
  sections: CanvasSection[]
  attribution?: CanvasAttribution
}

// Map icon name strings to Lucide React components
const ICON_MAP: Record<string, LucideIcon> = {
  'handshake': Handshake,
  'settings': Settings,
  'briefcase': Briefcase,
  'heart': Heart,
  'message-square': MessageSquare,
  'truck': Truck,
  'users': Users,
  'wallet': Wallet,
  'dollar-sign': DollarSign,
  'target': Target,
  'smile': Smile,
  'frown': Frown,
  'zap': Zap,
  'gift': Gift,
  'package': Package,
  'building-2': Building2,
  'shuffle': Shuffle,
  'sparkles': Sparkles,
  'alert-circle': AlertCircle,
  'clock': Clock,
  'bar-chart-3': BarChart3,
  'alert-triangle': AlertTriangle,
  'user': User,
  'cloud': Cloud,
  'sun': Sun,
  'star': Star,
}

function mapSection(def: CanvasSectionDef): CanvasSection {
  const theme = inferCanvasSectionTheme(def)
  const themeStyles = getCanvasSectionThemeStyles(theme)

  return {
    id: def.id,
    title: def.title,
    icon: ICON_MAP[def.iconName] || Heart,
    theme,
    color: themeStyles.color,
    bgColor: themeStyles.bgColor,
    classUris: def.classUris,
    gridArea: def.gridArea,
    description: def.description,
    sectionType: def.sectionType,
  }
}

function mapTemplate(def: CanvasTemplateDef): CanvasTemplate {
  return {
    id: def.id,
    name: def.name,
    description: def.description,
    gridColumns: def.gridColumns,
    gridRows: def.gridRows,
    gridAreas: def.gridAreas,
    sections: def.sections.map(mapSection),
    attribution: def.attribution,
  }
}

// Build templates from shared definitions
export const BUSINESS_MODEL_CANVAS: CanvasTemplate = mapTemplate(SHARED_TEMPLATES[0])
export const VALUE_PROPOSITION_CANVAS: CanvasTemplate = mapTemplate(SHARED_TEMPLATES[1])
export const POSITIONING_CANVAS: CanvasTemplate = mapTemplate(SHARED_TEMPLATES[2])
export const PROBLEM_STATEMENT_CANVAS: CanvasTemplate = mapTemplate(SHARED_TEMPLATES[3])
export const PERSONA_CANVAS: CanvasTemplate = mapTemplate(SHARED_TEMPLATES[4])

export const CANVAS_TEMPLATES: CanvasTemplate[] = SHARED_TEMPLATES.map(mapTemplate)

export function getCanvasTemplate(id: string): CanvasTemplate | undefined {
  return CANVAS_TEMPLATES.find((t) => t.id === id)
}

// Domain → which canvas templates are relevant
export type CanvasDomain = 'market' | 'business' | 'solution'

export const DEFAULT_DOMAIN_TEMPLATES: Record<CanvasDomain, string[]> = {
  market: ['persona-canvas', 'positioning-canvas', 'value-proposition-canvas'],
  business: ['business-model-canvas', 'value-proposition-canvas'],
  solution: ['problem-statement-canvas', 'value-proposition-canvas'],
}

export type CanvasDomainConfig = Record<CanvasDomain, string[]>

const STORAGE_KEY = (projectId: string) => `canvas-domain-config-${projectId}`

export function loadDomainConfig(projectId: string): CanvasDomainConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(projectId))
    if (raw) return JSON.parse(raw) as CanvasDomainConfig
  } catch {
    // ignore
  }
  return { ...DEFAULT_DOMAIN_TEMPLATES }
}

export function saveDomainConfig(projectId: string, config: CanvasDomainConfig): void {
  localStorage.setItem(STORAGE_KEY(projectId), JSON.stringify(config))
}

export function getTemplatesForDomain(
  domain: CanvasDomain,
  config: CanvasDomainConfig = DEFAULT_DOMAIN_TEMPLATES,
): CanvasTemplate[] {
  const ids = config[domain] ?? []
  return ids.map(id => CANVAS_TEMPLATES.find(t => t.id === id)).filter(Boolean) as CanvasTemplate[]
}
